import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verify the token that was present at app load (from localStorage). This runs
  // ONCE on mount — logging in afterwards sets the user directly and must NOT
  // trigger a re-verify, which previously raced the fresh login: it could
  // overwrite the user (dropping merged `tenants`) or, on a transient error,
  // call logout() and wipe a just-successful login.
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get('/api/auth/verify', {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        if (!cancelled) setUser(response.data.user);
      } catch (error) {
        logger.error('Token verification failed:', error);
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    logger.log('🔐 Login function called:', { username, baseURL: axios.defaults.baseURL });
    try {
      logger.log('📡 Sending POST request to /api/auth/login...');
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });
      logger.log('✅ Login response:', response.data);

      const { token: newToken, user: userData, tenants } = response.data;
      
      setToken(newToken);
      // Include tenants in user data if provided
      setUser(tenants ? { ...userData, tenants } : userData);
      localStorage.setItem('token', newToken);
      
      toast.success('Успешно се најавивте!');
      return { success: true };
    } catch (error) {
      logger.error('❌ Login error:', error);
      logger.error('❌ Error response:', error.response?.data);
      const message = error.response?.data?.error || 'Грешка при најавување';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    toast.info('Одјавени сте');
  };

  const updateProfile = async (profileData) => {
    try {
      await axios.put('/api/auth/profile', profileData);
      setUser(prev => ({ ...prev, ...profileData }));
      toast.success('Профилот е ажуриран!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Грешка при ажурирање на профилот';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.put('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Лозинката е променета!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Грешка при промена на лозинката';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      const { token: newToken, user: newUser, tenants } = response.data;
      
      setToken(newToken);
      setUser(tenants ? { ...newUser, tenants } : newUser);
      localStorage.setItem('token', newToken);
      
      toast.success('Успешна регистрација!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Грешка при регистрација';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
    isManager: ['superadmin', 'admin', 'manager'].includes(user?.role),
    isOperator: ['superadmin', 'admin', 'manager', 'operator'].includes(user?.role),
    isInstaller: user?.role === 'installer' || user?.role === 'superadmin',
    // Only a *pure* installer is auto-redirected to the installer screen;
    // superadmin keeps installer access but gets the full app (dashboard etc.).
    isInstallerOnly: user?.role === 'installer',
    isSuperAdmin: user?.role === 'superadmin',
    // Read-only presentation account: the backend (demoGuard) rejects every write,
    // so the UI hides the controls rather than inviting a click that always 403s.
    isDemo: user?.is_demo === 1 || user?.is_demo === true
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


















